import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import { PurchaseSummarySkeleton } from './PurchaseSummarySkeleton';
import { PurchaseSummaryView } from './PurchaseSummaryView';
import type { PurchaseSummary as PurchaseSummaryModel } from './purchaseSummaryMeta';
import { usePurchaseSummary } from './usePurchaseSummary';

type PurchaseSummaryContentProps = {
    data: PurchaseSummaryModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/**
 * Self-served purchase summary widget. Owns its data hook and is
 * wrapped in an `ErrorBoundary` so a render-time failure here cannot
 * break the surrounding page. While the hook is loading the container
 * renders the `Skeleton`, which mirrors the final layout to prevent
 * shift.
 *
 * The `throw error` for the boundary lives in the child
 * `PurchaseSummaryContent` because React error boundaries do not catch
 * errors thrown by the component that renders them — only by children.
 *
 * No `id` / `data-testid` props are accepted on this container — every
 * descendant uses literal static prefixes via `getNodeTestIdProp` so the
 * testids stay greppable in source.
 */
export const PurchaseSummary: React.FC = () => {
    const { data, error, isLoading, onRetry } = usePurchaseSummary();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('purchases.widgets.purchaseSummary.errorTitle'),
            errorMessage: t('purchases.widgets.purchaseSummary.errorMessage'),
            errorRetry: t('purchases.widgets.purchaseSummary.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="purchase-summary-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="purchase-summary-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <PurchaseSummaryContent
                data={data}
                error={error}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

const PurchaseSummaryContent: React.FC<PurchaseSummaryContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <PurchaseSummarySkeleton />;
    }

    return <PurchaseSummaryView data={data} />;
};
